import { call, put, takeLatest, select, delay } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import {
    initializeRequest,
    initializeSuccess,
    initializeFailure,
    signInRequest,
    signInSuccess,
    signInFailure,
    signUpRequest,
    signUpSuccess,
    signUpFailure,
    signOutRequest,
    signOutSuccess,
    signOutFailure,
    AuthCredentials,
    forgotPasswordRequest,
    forgotPasswordSuccess,
    forgotPasswordFailure,
    ForgotPasswordPayload,
    resetPasswordRequest,
    resetPasswordSuccess,
    resetPasswordFailure,
    codeExchangeRequest,
    codeExchangeFailure,
    clearPasswordResetCooldown,
} from '../slices/authSlice';

import { supabase } from '../../lib/supabase';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as SecureStore from 'expo-secure-store';
import { RootState } from '../index';
import Toast from 'react-native-toast-message';
import { Alert, Platform } from 'react-native';
import NavigationService from '../../services/NavigationService';

// Helper function to get the password reset redirect URL based on platform
function getPasswordResetRedirectUrl(): string {
  if (Platform.OS === 'web') {
    // For web, use the current origin
    if (typeof window !== 'undefined' && window.location?.origin) {
      return `${window.location.origin}/auth/password-reset`;
    }
    // Fallback to production URL if window is not available
    return 'https://jadetrade.app/auth/password-reset';
  }
  // For iOS and Android, use the deep link
  return 'com.jadetrade://password-reset';
}

//---------------------- INITIALIZE (check session) ----------------------
function* handleInitialize() {
    try {
        const { data, error } = yield call([supabase.auth, supabase.auth.getSession]);
        if (error) {
            throw new Error(error.message);
        }

        if (data?.session?.user) {
            const user = data.session.user;
            
            // Fetch user profile to get phone information
            const profileResult = yield call(async () => {
                const { data: profileData, error: profileError } = await supabase
                    .from('profiles')
                    .select('phone_number, phone_verified')
                    .eq('id', user.id)
                    .single();
                return { data: profileData, error: profileError };
            });
            
            const { data: profile } = profileResult;
            
            yield put(initializeSuccess({ 
                id: user.id, 
                email: user.email || user.user_metadata?.email || '',
                phone: profile?.phone_number || null,
                phone_verified: profile?.phone_verified || false
            }));
        } else {
            // No active session
            yield put(initializeFailure('No active session found'));
        }
    } catch (err: any) {
        yield put(initializeFailure(err.message));
    }
}

/** Clears Supabase auth state from device storage */
function* clearAuthState() {
    try {
        // Clear AsyncStorage
        yield call([AsyncStorage, AsyncStorage.multiRemove], [
            'supabase.auth.token',
            'supabase.auth.refreshToken',
            'supabase.auth.user',
            'supabase.auth.expires_at',
            'supabase.auth.expires_in',
        ]);

        // For SecureStore keys (native only) and localStorage (web)
        const authKeys = [
            'supabase.auth.token',
            'supabase.auth.refreshToken',
            'supabase.auth.user',
            'supabase.auth.expires_at',
            'supabase.auth.expires_in',
            'supabase.auth.provider_token',
            'supabase.auth.provider_refresh_token',
            'access_token',
            'refresh_token',
        ];
        
        if (Platform.OS === 'web') {
            // Web: clear from localStorage
            if (typeof window !== 'undefined') {
                for (const key of authKeys) {
                    try {
                        localStorage.removeItem(key);
                    } catch (e) {
                        // Ignore errors
                    }
                }
            }
        } else {
            // Native: clear from SecureStore
            for (const key of authKeys) {
                yield call([SecureStore, SecureStore.deleteItemAsync], key);
            }
        }

        // Actually sign out from Supabase
        const { error } = yield call([supabase.auth, supabase.auth.signOut]);
        if (error) {
            console.log('Error signing out from Supabase:', error);
        }
    } catch (error) {
        console.error('Error clearing auth state:', error);
    }
}

//---------------------- SIGN IN ----------------------
function* handleSignIn(action: PayloadAction<AuthCredentials>) {
    try {
        const { email, phone, password } = action.payload;
        
        // Determine if signing in with email or phone
        let signInCredentials;
        if (email) {
            signInCredentials = { email: email.trim(), password };
        } else if (phone) {
            signInCredentials = { phone: phone.trim(), password };
        } else {
            throw new Error('Either email or phone is required for sign in');
        }
        
        const { data, error } = yield call(
            [supabase.auth, supabase.auth.signInWithPassword],
            signInCredentials
        );

        if (error) {
            throw error;
        }
        if (data?.user && data?.session) {
            const user = data.user;
            const token = data.session.access_token;
            const refreshToken = data.session.refresh_token;

            // Fetch user profile to get phone information
            const profileResult = yield call(async () => {
                const { data: profileData, error: profileError } = await supabase
                    .from('profiles')
                    .select('phone_number, phone_verified')
                    .eq('id', user.id)
                    .single();
                return { data: profileData, error: profileError };
            });
            const { data: profile } = profileResult;

            yield put(
                signInSuccess({
                    user: {
                        id: user.id,
                        email: user.email ?? '',
                        phone: profile?.phone_number || null,
                        phone_verified: profile?.phone_verified || false,
                    },
                    token,
                    refreshToken,
                })
            );
            // Store tokens - use SecureStore on native, localStorage on web
            if (Platform.OS === 'web') {
                // Web: use localStorage (Supabase already handles this, but we store for consistency)
                if (typeof window !== 'undefined') {
                    try {
                        localStorage.setItem('access_token', data.session.access_token);
                        localStorage.setItem('refresh_token', data.session.refresh_token);
                    } catch (e) {
                        console.warn('Failed to store tokens in localStorage:', e);
                    }
                }
            } else {
                // Native: use SecureStore
                yield call(SecureStore.setItemAsync, 'access_token', data.session.access_token);
                yield call(SecureStore.setItemAsync, 'refresh_token', data.session.refresh_token);
            }
            
            // Track email/password sign-in in PostHog
            try {
                const { trackEvent, identifyUser } = require('../../services/posthog');
                trackEvent('user_signed_in', {
                    user_id: user.id,
                    email: user.email || 'unknown',
                    method: email ? 'email' : 'phone',
                });
                identifyUser(user.id, {
                    email: user.email || 'unknown',
                });
            } catch (error) {
                console.warn('⚠️ [POSTHOG] Failed to track email sign-in:', error);
            }
        } else {
            throw new Error('No user returned from signIn');
        }
    } catch (err: any) {
        
        // Handle specific error cases
        if (err.message?.includes('Email not confirmed')) {
            Alert.alert(
                'Email Not Confirmed',
                'Please check your email and click the confirmation link to verify your account.',
                [{ text: 'OK' }]
            );
        } else {
            Toast.show({
                type: 'error',
                text1: 'Sign In Error',
                text2: err.message || 'Something went wrong',
            });
        }
        
        yield put(signInFailure(err.message || 'Sign in failed'));
    }
}

//---------------------- SIGN UP ----------------------
function* handleSignUp(action: PayloadAction<AuthCredentials>) {
    try {
        const { email, phone, password } = action.payload;
        yield call(clearAuthState);

        // DISABLED: Phone verification temporarily disabled
        // if (!email || !phone) {
        //     throw new Error('Both email and phone are required for sign up');
        // }
        
        if (!email) {
            throw new Error('Email is required for sign up');
        }

        // Step 1: Create user with email/password - DISABLED: Phone verification disabled
        const { data, error } = yield call([supabase.auth, supabase.auth.signUp], {
            email: email.trim(),
            password,
            // DISABLED: Phone verification temporarily disabled
            // options: {
            //     data: {
            //         phone_number: phone
            //     }
            // }
        });

        console.log('SignUp data:', data, 'error:', error);

        if (error) {
            throw error;
        }

        if (data.user) {
            // DISABLED: Phone verification temporarily disabled
            // Step 2: Save phone number to profile with unverified status
            /*
            const { error: profileError } = yield call(
                () => supabase
                    .from('profiles')
                    .update({
                        phone_number: phone,
                        phone_verified: false
                    })
                    .eq('id', data.user.id)
            );
            
            if (profileError) {
                console.error('Profile update error:', profileError);
            }
            */

            // Email verification restored - navigate to confirmation screen
            yield put(signUpSuccess(data));
            console.log('User signed up successfully - email confirmation sent:', data.user);
            
            // Track user sign up in PostHog
            try {
                const { trackEvent, identifyUser } = require('../../services/posthog');
                trackEvent('user_signed_up', {
                    user_id: data.user.id,
                    email: email.trim(),
                    method: 'email',
                });
                identifyUser(data.user.id, {
                    email: email.trim(),
                });
                console.log('✅ [POSTHOG] Tracked email sign up for new user');
            } catch (error) {
                console.warn('⚠️ [POSTHOG] Failed to track sign up:', error);
            }
            
            // Navigate to email confirmation screen instead of phone verification
            yield call(() => NavigationService.navigate('AuthFlow', { screen: 'SignUpConfirmation' }));
        }
    } catch (err: any) {
        console.error('Sign up error:', err);
        Toast.show({
            type: 'error',
            text1: 'Sign Up Error',
            text2: err.message || 'Something went wrong',
        });
        yield put(signUpFailure(err.message || 'Sign up failed'));
    }
}

//---------------------- SIGN OUT ----------------------
function* handleSignOut() {
    try {
        yield call(clearAuthState);
        yield put(signOutSuccess());
    } catch (err: any) {
        yield put(signOutFailure(err.message || 'Sign out failed'));
    }
}


function* handleForgotPassword(action: PayloadAction<ForgotPasswordPayload>) {
  try {
    const { email } = action.payload;

    // Check rate limiting
    const state: RootState = yield select();
    const { lastPasswordResetRequest, passwordResetCooldown } = state.auth;

    if (passwordResetCooldown && lastPasswordResetRequest) {
      const timeSinceLastRequest = Date.now() - lastPasswordResetRequest;
      const cooldownPeriod = 2 * 60 * 1000; // 2 minutes in milliseconds

      if (timeSinceLastRequest < cooldownPeriod) {
        const remainingTime = Math.ceil((cooldownPeriod - timeSinceLastRequest) / 1000);
        const minutes = Math.floor(remainingTime / 60);
        const seconds = remainingTime % 60;
        const timeString = minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
        
        yield put(forgotPasswordFailure(`Please wait ${timeString} before requesting another reset email.`));
        Toast.show({
          type: 'error',
          text1: 'Too many requests',
          text2: `Please wait ${timeString} before trying again.`,
        });
        return;
      }
    }

    const redirectTo = getPasswordResetRedirectUrl();
    console.log('🔐 [PASSWORD-RESET] Using redirect URL:', redirectTo);
    
    const { error } = yield call(
      [supabase.auth, supabase.auth.resetPasswordForEmail],
      email,
      {
        redirectTo
      }
    );
    if (error) throw error;

    yield put(forgotPasswordSuccess());

    // Start a timer to clear the cooldown after 2 minutes
    yield delay(2 * 60 * 1000); // 2 minutes
    yield put(clearPasswordResetCooldown());
  } catch (err: any) {
    console.error('Forgot password error:', err);
    yield put(forgotPasswordFailure(err.message || 'Failed to send reset email'));
    Toast.show({
      type: 'error',
      text1: 'Password reset failed',
      text2: err.message || 'Try again later.',
    });
  }
}

function* handleResetPassword(action: PayloadAction<{ newPassword: string }>) {
    try {
        const { newPassword } = action.payload;
        const { data, error } = yield call([supabase.auth, supabase.auth.updateUser], {
            password: newPassword,
        });
        if (error) {
            throw new Error(error.message);
        }

        yield put(resetPasswordSuccess());

        Toast.show({
            type: 'success',
            text1: 'Password Reset',
            text2: 'Your password has been updated successfully.',
        });
    } catch (err: any) {
        yield put(resetPasswordFailure(err.message || 'Failed to reset password'));
        Toast.show({
            type: 'error',
            text1: 'Reset Error',
            text2: err.message || 'Something went wrong',
        });
    }
}

function* handleCodeExchange(action: PayloadAction<{ code: string }>) {
    try {
        const { code } = action.payload;
        yield call([supabase.auth, supabase.auth.exchangeCodeForSession], code);
        yield* handleInitialize();
    } catch (err: any) {
        console.error('Code exchange error:', err);
        yield put(codeExchangeFailure(err.message || 'Failed to exchange code'));
    }
}

//---------------------- WATCHERS ----------------------
export function* authSaga() {
    yield takeLatest(initializeRequest.type, handleInitialize);
    yield takeLatest(signInRequest.type, handleSignIn);
    yield takeLatest(signUpRequest.type, handleSignUp);
    yield takeLatest(signOutRequest.type, handleSignOut);
    yield takeLatest(forgotPasswordRequest.type, handleForgotPassword);
    yield takeLatest(resetPasswordRequest.type, handleResetPassword);
    yield takeLatest(codeExchangeRequest.type, handleCodeExchange);
}